import React, { useCallback, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * "You have unsaved changes."
 *
 * An estimate lives in memory until Save writes a JobVersion. Starting a new
 * estimate, opening another job, or loading an older version all replace the
 * form wholesale — and used to do it instantly, taking an afternoon of
 * untyped-again work with them. The tab-close guard already knew the form was
 * dirty; this asks the same question for the three in-app doors.
 *
 * Cancel is the default action: the safe answer is always "keep my work".
 */
function UnsavedChangesDialog({
  open = false,
  destination = null,
  onDiscard,
  onCancel,
}) {
  const cancelRef = useRef(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Dismissing (Esc, overlay, the X) is never a discard.
        if (!nextOpen) onCancel?.();
      }}
    >
      <DialogContent
        onOpenAutoFocus={(event) => {
          // Land on Cancel, not on the destructive button.
          event.preventDefault();
          cancelRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Unsaved changes</DialogTitle>
          <DialogDescription>
            You have unsaved changes on this estimate. Leaving now will discard
            them.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {/* The button names the door it opens — "Discard and open Harbor
              Shutters" is a decision; "Discard and continue" is a shrug. */}
          <Button
            type="button"
            variant="outline"
            className="unsaved-changes__discard"
            onClick={onDiscard}
          >
            {destination
              ? `Discard and ${destination}`
              : "Discard and continue"}
          </Button>
          <Button
            type="button"
            ref={cancelRef}
            className="unsaved-changes__cancel"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Wrap a handler so it asks before throwing unsaved work away.
 *
 * `guard(handler, { destination })` returns a handler with the same call
 * signature. A clean form is passed straight through, return value and all —
 * the common case pays nothing, and Jobs → Open still awaits the real load.
 *
 * When the form is dirty the call is PARKED and the wrapper returns
 * immediately with `Promise.resolve(undefined)`. That matters: a caller that
 * shows a pending state around the await (the Jobs page sets "Opening…") must
 * not sit in it for however long the estimator takes to answer a modal about
 * something else. Nothing has happened yet, so reporting "nothing happened" is
 * the honest answer.
 *
 * The consequence is that a parked action's outcome can no longer reach its
 * original caller, so a failure after Discard would otherwise vanish. It is
 * routed to the `onError` option instead — the one place that still has a way
 * to tell the estimator.
 *
 * `destination` names the door for the dialog's confirm button ("Discard and
 * open Harbor Shutters"). It is a string, or a function of the handler's own
 * arguments returning one, so the label can name the job or version actually
 * being opened.
 *
 * `isDirty` is a predicate, not a boolean, so it is read at click time rather
 * than captured at render time.
 */
export function useUnsavedChangesGuard(isDirty, { onError } = {}) {
  const [pending, setPending] = useState(null);

  const guard = useCallback(
    (action, { destination } = {}) =>
      (...args) => {
        if (typeof isDirty !== "function" || !isDirty()) {
          return action(...args);
        }
        setPending(() => ({
          destination:
            typeof destination === "function"
              ? destination(...args)
              : destination,
          run: () => {
            setPending(null);
            try {
              // The caller is long gone; a rejection here has nowhere else to
              // go, so it is handed to onError rather than left unhandled.
              Promise.resolve(action(...args)).catch((error) =>
                onError?.(error),
              );
            } catch (error) {
              onError?.(error);
            }
          },
          cancel: () => setPending(null),
        }));
        return Promise.resolve(undefined);
      },
    [isDirty, onError],
  );

  const dialogProps = {
    open: Boolean(pending),
    destination: pending?.destination,
    onDiscard: () => pending?.run(),
    onCancel: () => pending?.cancel(),
  };

  return { guard, dialogProps };
}

export default UnsavedChangesDialog;
